import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GifPlayPauseImage } from './GifPlayPauseImage';
import { isAnimatedGifUrl } from './isAnimatedGifUrl';
import { sniffIsGifMedia } from './sniffIsGifMedia';

type ImgProps = {
  src: string;
  alt: string;
  'aria-hidden'?: boolean;
  className?: string;
  [key: string]: unknown;
};

type Props = {
  url: string;
  alt: string;
  ariaHidden?: boolean;
  /**
   * When true (Cards6), probe extensionless CDN URLs for GIF magic/Content-Type.
   * Starts with controls + paused so animated GIFs never autoplay during the
   * probe; drops controls when sniff proves a static image, or when freeze
   * capture fails after sniff did not confirm a GIF.
   */
  enableAnimationPause?: boolean;
  renderImg: (props: ImgProps) => React.ReactNode;
};

/**
 * Chooses plain <img> vs GifPlayPauseImage.
 * Extensionless EasyLlama CDN URLs need `enableAnimationPause` + sniffing.
 */
export const MaybeAnimatedImage = ({
  url,
  alt,
  ariaHidden,
  enableAnimationPause,
  renderImg
}: Props) => {
  const knownGif = isAnimatedGifUrl(url);
  const [showControls, setShowControls] = useState(
    () => knownGif || Boolean(enableAnimationPause)
  );
  /** `true` only after sniff confirms GIF (not fail-open). */
  const [sniffConfirmedGif, setSniffConfirmedGif] = useState(knownGif);
  /** False while extensionless probe is in flight. */
  const [sniffSettled, setSniffSettled] = useState(
    knownGif || !enableAnimationPause
  );
  const freezeFailedRef = useRef(false);
  // Keep in sync for freeze callbacks that may run in the same turn as sniff.
  const sniffSettledRef = useRef(sniffSettled);
  const sniffConfirmedGifRef = useRef(sniffConfirmedGif);

  useEffect(() => {
    let cancelled = false;
    freezeFailedRef.current = false;

    if (isAnimatedGifUrl(url)) {
      sniffSettledRef.current = true;
      sniffConfirmedGifRef.current = true;
      setShowControls(true);
      setSniffConfirmedGif(true);
      setSniffSettled(true);
      return;
    }

    if (!enableAnimationPause) {
      sniffSettledRef.current = true;
      sniffConfirmedGifRef.current = false;
      setShowControls(false);
      setSniffConfirmedGif(false);
      setSniffSettled(true);
      return;
    }

    sniffSettledRef.current = false;
    sniffConfirmedGifRef.current = false;
    setSniffConfirmedGif(false);
    setSniffSettled(false);
    // Fail-open: controls already shown (startPaused) so GIFs never autoplay
    // during the probe. Drop controls when sniff proves a static image.
    sniffIsGifMedia(url).then(result => {
      if (cancelled) return;
      // Update refs before setState so a same-turn freeze failure sees settled.
      sniffSettledRef.current = true;
      setSniffSettled(true);
      if (result === false) {
        sniffConfirmedGifRef.current = false;
        setShowControls(false);
        setSniffConfirmedGif(false);
        return;
      }
      if (result === true) {
        sniffConfirmedGifRef.current = true;
        setShowControls(true);
        setSniffConfirmedGif(true);
        return;
      }
      // Inconclusive: keep fail-open only if freeze still works / pending.
      sniffConfirmedGifRef.current = false;
      setSniffConfirmedGif(false);
      setShowControls(!freezeFailedRef.current);
    });

    return () => {
      cancelled = true;
    };
  }, [url, enableAnimationPause]);

  const onFreezeUnavailable = useCallback(() => {
    freezeFailedRef.current = true;
    // Wait for sniff to settle — dropping controls mid-probe would remount a
    // plain <img> and let a real GIF autoplay without a pause control.
    // Confirmed GIFs keep controls (hide-when-paused still applies).
    // Refs avoid a stale closure when sniff + freeze resolve in the same turn.
    if (
      enableAnimationPause &&
      !isAnimatedGifUrl(url) &&
      sniffSettledRef.current &&
      !sniffConfirmedGifRef.current
    ) {
      setShowControls(false);
    }
  }, [enableAnimationPause, url]);

  if (showControls) {
    return (
      <GifPlayPauseImage
        url={url}
        alt={alt}
        ariaHidden={ariaHidden}
        startPaused={Boolean(enableAnimationPause)}
        // While sniff is pending, hide on freeze failure so a GIF cannot
        // animate under a "paused" control before we know the media type.
        hideWhenFreezeUnavailable={
          knownGif || sniffConfirmedGif || !sniffSettled
        }
        onFreezeUnavailable={onFreezeUnavailable}
        renderImg={renderImg}
      />
    );
  }

  return (
    <>{renderImg({ src: url, alt: alt || '', 'aria-hidden': ariaHidden })}</>
  );
};
